import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import axios from "axios";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { baseUrl } from "../Univ.js";

/* =========================================================
   HELPERS
========================================================= */

const pad = (n) => String(n).padStart(2, "0");

const formatDate = (date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const toInputValue = (date) =>
    `${formatDate(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;

const startOfDay = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

const toNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;

    const number = Number(String(value).replace(/,/g, "").trim());
    return Number.isFinite(number) ? number : 0;
};

const currency = (value) =>
    toNumber(value).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const normalize = (value) =>
    String(value ?? "")
        .trim()
        .replace(/\s+/g, " ")
        .toLowerCase();

const splitCsv = (value) =>
    Array.isArray(value)
        ? value.map((item) => String(item ?? "").trim())
        : String(value ?? "")
              .split(",")
              .map((item) => item.trim());

const isNumericId = (value) => /^\d+$/.test(String(value ?? "").trim());

const firstValue = (obj, keys, fallback = null) => {
    for (const key of keys) {
        if (obj?.[key] !== undefined && obj?.[key] !== null &&
            obj?.[key] !== "") {
            return obj[key];
        }
    }
    return fallback;
};

const parseDateTime = (dateValue, timeValue) => {
    if (!dateValue) return null;

    const dateString = String(dateValue).trim();
    const timeString = String(timeValue || "00:00:00")
        .trim()
        .split(".")[0];

    if (dateString.includes("T") || dateString.includes(" ")) {
        const parsed = new Date(dateString.replace(" ", "T"));
        if (!Number.isNaN(parsed.getTime())) return parsed;
    }

    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (match) {
        const [, year, month, day] = match;
        const time = timeString.match(
            /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/
        );

        return new Date(
            Number(year),
            Number(month) - 1,
            Number(day),
            Number(time?.[1] || 0),
            Number(time?.[2] || 0),
            Number(time?.[3] || 0)
        );
    }

    const parsed = new Date(dateString);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getDateTime = (record) =>
    parseDateTime(
        firstValue(record, [
            "add_date_date",
            "grn_date",
            "purchase_date",
            "add_date",
            "sale_date",
            "invoice_date",
            "date",
            "created_at",
            "created",
        ]),
        firstValue(record, [
            "add_time",
            "grn_time",
            "purchase_time",
            "sale_time",
            "time",
        ])
    );

const displayDateTime = (value) => {
    if (!value) return "—";

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) return "—";

    return (
        `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/` +
        `${date.getFullYear()} ${pad(date.getHours())}:` +
        `${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    );
};

const batchKey = (productId, product, batch) =>
    `${normalize(productId || product)}|${normalize(batch)}`;

const addMovement = (map, key, qty, amount = 0, when = null) => {
    const previous = map.get(key) || {
        qty: 0,
        amount: 0,
        lastDate: null,
    };

    previous.qty += qty;
    previous.amount += amount;

    if (when && (!previous.lastDate || when > previous.lastDate)) {
        previous.lastDate = when;
    }

    map.set(key, previous);
};

/* =========================================================
   GRID COLUMNS
========================================================= */

const COLUMNS = [
    { field: "productId", headerName: "Product ID", width: 105 },

    {
        field: "product",
        headerName: "Drug / Product",
        minWidth: 230,
        flex: 1,
    },

    { field: "batch", headerName: "Batch", width: 125 },

    { field: "pack", headerName: "Pack", width: 75 },

    { field: "expiry", headerName: "Expiry", width: 110 },

    {
        field: "mrp",
        headerName: "MRP (₹)",
        width: 110,
        type: "number",
        valueFormatter: (value) => currency(value),
    },

    {
        field: "openingStock",
        headerName: "Opening",
        width: 100,
        type: "number",
    },

    {
        field: "stockReceived",
        headerName: "Stock In",
        width: 95,
        type: "number",
    },

    {
        field: "lastGrnDate",
        headerName: "Last GRN Date / Time",
        width: 175,
        valueFormatter: (value) => displayDateTime(value),
        sortComparator: (a, b) =>
            (a ? new Date(a).getTime() : 0) -
            (b ? new Date(b).getTime() : 0),
    },

    {
        field: "soldQty",
        headerName: "Sold Qty",
        width: 95,
        type: "number",
    },

    {
        field: "soldAmount",
        headerName: "Sold Amount (₹)",
        width: 145,
        type: "number",
        valueFormatter: (value) => currency(value),
    },

    {
        field: "lastSaleDate",
        headerName: "Last Sale Date / Time",
        width: 175,
        valueFormatter: (value) => displayDateTime(value),
        sortComparator: (a, b) =>
            (a ? new Date(a).getTime() : 0) -
            (b ? new Date(b).getTime() : 0),
    },

    {
        field: "closingStock",
        headerName: "Closing",
        width: 100,
        type: "number",
    },

    {
        field: "currentStock",
        headerName: "Current",
        width: 100,
        type: "number",
    },
];

const GRID_SX = {
    border: 1,
    borderColor: "divider",
    "& .MuiDataGrid-columnHeaders": {
        backgroundColor: "#f0fdf4",
        color: "#166534",
        fontWeight: 700,
    },
    "& .MuiDataGrid-cell": {
        fontSize: "0.84rem",
    },
};

/* =========================================================
   COMPONENT
========================================================= */

const DrugMovementRegister = () => {
    const [fromValue, setFromValue] = useState(() =>
        toInputValue(startOfDay(new Date()))
    );

    const [toValue, setToValue] = useState(() =>
        toInputValue(new Date())
    );

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [report, setReport] = useState(null);
    const [diag, setDiag] = useState(null);

    const requestId = useRef(0);
    const mounted = useRef(false);

    useEffect(() => {
        mounted.current = true;

        return () => {
            mounted.current = false;
            requestId.current += 1;
        };
    }, []);

    const applyPreset = (kind) => {
        const now = new Date();
        const today = startOfDay(now);

        if (kind === "today") {
            setFromValue(toInputValue(today));
            setToValue(toInputValue(now));
        }

        if (kind === "yesterday") {
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            setFromValue(toInputValue(yesterday));
            setToValue(
                toInputValue(new Date(today.getTime() - 1000))
            );
        }

        if (kind === "7d") {
            const weekStart = new Date(today);
            weekStart.setDate(weekStart.getDate() - 6);

            setFromValue(toInputValue(weekStart));
            setToValue(toInputValue(now));
        }
    };

    const generateReport = useCallback(async () => {
        const from = fromValue ? new Date(fromValue) : null;
        const to = toValue ? new Date(toValue) : null;

        if (
            !from ||
            !to ||
            Number.isNaN(from.getTime()) ||
            Number.isNaN(to.getTime())
        ) {
            setError("Please select both From and To date/time.");
            return;
        }

        if (from > to) {
            setError("From date/time cannot be after To date/time.");
            return;
        }

        if (to > new Date()) {
            setError("To date/time cannot be in the future.");
            return;
        }

        const request = ++requestId.current;

        setLoading(true);
        setError("");
        setRows([]);
        setReport(null);
        setDiag(null);

        try {
            /*
             * Fetch from the beginning of the selected day through today.
             * We need transactions after the selected To time to estimate
             * the stock balance at the selected To time.
             */
            const params = {
                start_date: formatDate(from),
                end_date: formatDate(new Date()),
                status: "1",
                location: 1,
            };

            const [stockRes, grnRes, gstRes] = await Promise.all([
                axios.get(`${baseUrl}indent-pharm-search/`, {
                    params: {
                        qty: "gt0",
                        location: 1,
                    },
                }),

                axios.get(`${baseUrl}all-pharm-grn/`, {
                    params,
                }),

                axios.get(`${baseUrl}all-pharm-sale-gst/`, {
                    params,
                }),
            ]);

            if (request !== requestId.current || !mounted.current) {
                return;
            }

            const stockData = Array.isArray(stockRes.data)
                ? stockRes.data
                : stockRes.data?.results;

            const grnData = Array.isArray(grnRes.data)
                ? grnRes.data
                : grnRes.data?.results;

            const salesData = Array.isArray(gstRes.data)
                ? gstRes.data
                : gstRes.data?.results;

            if (
                !Array.isArray(stockData) ||
                !Array.isArray(grnData) ||
                !Array.isArray(salesData)
            ) {
                throw new Error(
                    "Unexpected API response. Verify stock, GRN and sales serializers."
                );
            }

            /* -----------------------------------------------
               1. NORMALIZE STOCK
            ------------------------------------------------ */

            const stockMap = new Map();

            stockData.forEach((item) => {
                const productObject =
                    item.product && typeof item.product === "object"
                        ? item.product
                        : {};

                const productId = String(
                    firstValue(productObject, ["id", "product_id"]) ??
                    firstValue(item, ["product_id", "drug_id"]) ??
                    (typeof item.product === "number"
                        ? item.product
                        : item.id ?? "")
                );

                const product = String(
                    firstValue(productObject, [
                        "product",
                        "name",
                        "drug_name",
                    ]) ??
                    firstValue(item, [
                        "product_name",
                        "drug_name",
                        "name",
                    ]) ??
                    (typeof item.product === "string" &&
                    !isNumericId(item.product)
                        ? item.product
                        : "N/A")
                );

                const batch = String(
                    firstValue(productObject, ["batch", "batch_no"]) ??
                    firstValue(item, ["batch", "batch_no"]) ??
                    ""
                );

                const key = batchKey(productId, product, batch);

                const currentStock = toNumber(
                    firstValue(item, ["qty", "current_qty", "balance"]) ??
                    firstValue(productObject, ["qty", "current_qty"]) ??
                    0
                );

                const normalized = {
                    productId,
                    product,
                    batch,
                    pack:
                        firstValue(productObject, ["pack", "packing"]) ??
                        firstValue(item, ["pack", "packing"]) ??
                        "N/A",
                    expiry:
                        firstValue(productObject, [
                            "expiry",
                            "expiry_date",
                        ]) ??
                        firstValue(item, ["expiry", "expiry_date"]) ??
                        "N/A",
                    mrp: toNumber(
                        firstValue(productObject, ["mrp", "price"]) ??
                        firstValue(item, ["mrp", "price"]) ??
                        0
                    ),
                    currentStock,
                    key,
                };

                /*
                 * Do not add duplicate stock records together.
                 * The stock API may repeat a product/batch.
                 * Keep one record, using the greatest reported balance.
                 */
                const existing = stockMap.get(key);

                if (!existing || currentStock > existing.currentStock) {
                    stockMap.set(key, normalized);
                }
            });

            /* -----------------------------------------------
               2. NORMALIZE GRN PURCHASE RECEIPTS
            ------------------------------------------------ */

            const grnMovements = new Map();

            let validGrnDates = 0;
            let grnLines = 0;

            grnData.forEach((grn) => {
                const when = getDateTime(grn);

                if (!when) return;

                validGrnDates++;

                /*
                 * Supported formats:
                 *   product: "12,15,20", qty: "5,10,2"
                 *   product_id: 12, qty: 5
                 *   detail/product line objects
                 *
                 * Adapt the candidate fields below if your GRN serializer
                 * uses different names.
                 */
                const nestedLines =
                    grn.items ??
                    grn.details ??
                    grn.grn_items ??
                    grn.lines;

                let lines = [];

                if (Array.isArray(nestedLines) && nestedLines.length) {
                    lines = nestedLines.map((line) => ({
                        product: firstValue(line, [
                            "product",
                            "product_id",
                            "drug_id",
                            "product_name",
                        ]),
                        batch: firstValue(line, ["batch", "batch_no"], ""),
                        qty: toNumber(
                            firstValue(line, [
                                "received_qty",
                                "grn_qty",
                                "quantity",
                                "qty",
                                "totqty",
                            ])
                        ),
                    }));
                } else {
                    const products = splitCsv(
                        firstValue(grn, [
                            "product",
                            "product_id",
                            "drug_id",
                            "product_name",
                        ], "")
                    );

                    const quantities = splitCsv(
                        firstValue(grn, [
                            "received_qty",
                            "grn_qty",
                            "quantity",
                            "qty",
                            "totqty",
                        ], "")
                    );

                    const batches = splitCsv(
                        firstValue(grn, ["batch", "batch_no"], "")
                    );

                    lines = products.map((product, index) => ({
                        product,
                        batch: batches[index] || "",
                        qty: toNumber(quantities[index]),
                    }));
                }

                lines.forEach((line) => {
                    if (!line.product || line.qty <= 0) return;

                    const productText = String(line.product).trim();
                    const batch = String(line.batch || "").trim();

                    let matched = [];

                    if (isNumericId(productText)) {
                        matched = [...stockMap.values()].filter(
                            (item) =>
                                item.productId === productText &&
                                (!batch ||
                                    normalize(item.batch) === normalize(batch))
                        );
                    } else {
                        matched = [...stockMap.values()].filter(
                            (item) =>
                                normalize(item.product) ===
                                    normalize(productText) &&
                                (!batch ||
                                    normalize(item.batch) === normalize(batch))
                        );
                    }

                    /*
                     * A GRN with an exact batch match is safest.
                     * Do not guess a batch when multiple candidates exist.
                     */
                    if (matched.length !== 1) return;

                    const key = matched[0].key;

                    if (when < from) return;

                    addMovement(
                        grnMovements,
                        key,
                        line.qty,
                        0,
                        when
                    );

                    grnLines++;
                });
            });

            /* -----------------------------------------------
               3. PROCESS SALES
            ------------------------------------------------ */

            const salesMovements = new Map();
            const salesAfterTo = new Map();
            const salesDetails = new Map();

            let validSaleDates = 0;
            let salesLines = 0;
            let unmatchedSales = 0;

            salesData.forEach((sale) => {
                const when = getDateTime(sale);

                if (!when) return;

                validSaleDates++;

                if (when < from) return;

                const products = splitCsv(
                    firstValue(sale, [
                        "product",
                        "product_id",
                        "drug_id",
                        "product_name",
                    ], "")
                );

                const quantities = splitCsv(
                    firstValue(sale, ["qty", "quantity", "sold_qty"], "")
                );

                const amounts = splitCsv(
                    firstValue(sale, [
                        "netamount",
                        "net_amount",
                        "amount",
                        "total",
                    ], "")
                );

                const batches = splitCsv(
                    firstValue(sale, ["batch", "batch_no"], "")
                );

                products.forEach((productValue, index) => {
                    if (!productValue) return;

                    const productText = String(productValue).trim();
                    const batch = batches[index] || "";

                    let matched = [];

                    if (isNumericId(productText)) {
                        matched = [...stockMap.values()].filter(
                            (item) =>
                                item.productId === productText &&
                                (!batch ||
                                    normalize(item.batch) === normalize(batch))
                        );
                    } else {
                        matched = [...stockMap.values()].filter(
                            (item) =>
                                normalize(item.product) ===
                                    normalize(productText) &&
                                (!batch ||
                                    normalize(item.batch) === normalize(batch))
                        );
                    }

                    const qty = toNumber(quantities[index]);
                    const amount = toNumber(amounts[index]);

                    if (matched.length !== 1) {
                        unmatchedSales++;
                        return;
                    }

                    const item = matched[0];
                    const key = item.key;

                    const target =
                        when <= to ? salesMovements : salesAfterTo;

                    addMovement(target, key, qty, amount, when);

                    const detail = salesDetails.get(key) || {
                        lastSaleDate: null,
                    };

                    if (
                        !detail.lastSaleDate ||
                        when > detail.lastSaleDate
                    ) {
                        detail.lastSaleDate = when;
                    }

                    salesDetails.set(key, detail);
                    salesLines++;
                });
            });

            /* -----------------------------------------------
               4. BUILD ONE ROW PER PRODUCT/BATCH
            ------------------------------------------------ */

            const built = [];

            const allKeys = new Set([
                ...stockMap.keys(),
                ...grnMovements.keys(),
                ...salesMovements.keys(),
            ]);

            allKeys.forEach((key) => {
                const stock = stockMap.get(key);
                const purchased = grnMovements.get(key) || {
                    qty: 0,
                    lastDate: null,
                };

                const sold = salesMovements.get(key) || {
                    qty: 0,
                    amount: 0,
                };

                const soldAfter = salesAfterTo.get(key) || {
                    qty: 0,
                    amount: 0,
                };

                const currentStock = stock?.currentStock ?? 0;

                /*
                 * Reconstruct balances at the selected To timestamp.
                 * This estimate assumes no unrecorded returns, transfers,
                 * wastage or manual stock adjustments.
                 */
                const closingStock = Math.max(
                    0,
                    currentStock + soldAfter.qty
                );

                const stockIn = purchased.qty;

                const openingStock = Math.max(
                    0,
                    closingStock + sold.qty - stockIn
                );

                /*
                 * Exclude rows with no movement in the selected period.
                 * Products with sales or GRN receipts are included.
                 */
                if (sold.qty === 0 && stockIn === 0) return;

                const detail = salesDetails.get(key);

                built.push({
                    id: key,
                    productId: stock?.productId || "N/A",
                    product: stock?.product || "N/A",
                    batch: stock?.batch || "N/A",
                    pack: stock?.pack || "N/A",
                    expiry: stock?.expiry || "N/A",
                    mrp: stock?.mrp || 0,
                    openingStock,
                    stockReceived: stockIn,
                    lastGrnDate: purchased.lastDate,
                    soldQty: sold.qty,
                    soldAmount: sold.amount,
                    lastSaleDate: detail?.lastSaleDate || null,
                    closingStock,
                    currentStock,
                });
            });

            if (request !== requestId.current || !mounted.current) {
                return;
            }

            setRows(built);
            setDiag({
                stockRecords: stockData.length,
                uniqueStockBatches: stockMap.size,
                grnRecords: grnData.length,
                validGrnDates,
                grnLines,
                salesRecords: salesData.length,
                validSaleDates,
                salesLines,
                unmatchedSales,
            });

            setReport({ from, to });
        } catch (err) {
            if (request !== requestId.current || !mounted.current) {
                return;
            }

            console.error("Drug Movement Register error:", err);

            setError(
                err.response?.data?.detail ||
                    err.message ||
                    "Unable to generate the Drug Movement Register."
            );
        } finally {
            if (request === requestId.current && mounted.current) {
                setLoading(false);
            }
        }
    }, [fromValue, toValue]);

    const handleClear = () => {
        requestId.current += 1;

        setFromValue(toInputValue(startOfDay(new Date())));
        setToValue(toInputValue(new Date()));
        setRows([]);
        setReport(null);
        setDiag(null);
        setError("");
        setLoading(false);
    };

    /* -----------------------------------------------
       TOTALS
    ------------------------------------------------ */

    const totals = useMemo(
        () =>
            rows.reduce(
                (total, row) => ({
                    openingStock:
                        total.openingStock + row.openingStock,
                    stockReceived:
                        total.stockReceived + row.stockReceived,
                    soldQty: total.soldQty + row.soldQty,
                    soldAmount: total.soldAmount + row.soldAmount,
                    closingStock:
                        total.closingStock + row.closingStock,
                }),
                {
                    openingStock: 0,
                    stockReceived: 0,
                    soldQty: 0,
                    soldAmount: 0,
                    closingStock: 0,
                }
            ),
        [rows]
    );

    const cards = [
        {
            label: "Opening Stock",
            value: totals.openingStock,
            color: "#1d4ed8",
        },
        {
            label: "Stock Received",
            value: totals.stockReceived,
            color: "#0891b2",
        },
        {
            label: "Sold Quantity",
            value: totals.soldQty,
            color: "#ea580c",
        },
        {
            label: "Sales Amount",
            value: `₹ ${currency(totals.soldAmount)}`,
            color: "#7c3aed",
        },
        {
            label: "Closing Stock",
            value: totals.closingStock,
            color: "#15803d",
        },
    ];

    const nowValue = toInputValue(new Date());

    return (
        <section className="container-fluid mt-4" translate="no">
            <div
                className="p-3 mb-4 rounded shadow-sm"
                style={{
                    background:
                        "linear-gradient(110deg, #ecfdf5, #eff6ff)",
                    borderLeft: "5px solid #16a34a",
                }}
            >
                <h3 className="mb-1" style={{ color: "#166534" }}>
                    <i className="fa-solid fa-capsules me-2" />
                    Drug Movement Register
                </h3>

                <div className="text-muted">
                    Batch-wise opening stock, GRN receipts, sales and closing stock
                </div>
            </div>

            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                    <div className="row g-3 align-items-end">
                        <div className="col-md-3">
                            <label
                                className="form-label fw-semibold"
                                htmlFor="dmr-from"
                            >
                                From Date &amp; Time
                            </label>

                            <input
                                id="dmr-from"
                                type="datetime-local"
                                className="form-control"
                                value={fromValue}
                                max={toValue || nowValue}
                                onChange={(event) =>
                                    setFromValue(event.target.value)
                                }
                            />
                        </div>

                        <div className="col-md-3">
                            <label
                                className="form-label fw-semibold"
                                htmlFor="dmr-to"
                            >
                                To Date &amp; Time
                            </label>

                            <input
                                id="dmr-to"
                                type="datetime-local"
                                className="form-control"
                                value={toValue}
                                min={fromValue || undefined}
                                max={nowValue}
                                onChange={(event) =>
                                    setToValue(event.target.value)
                                }
                            />
                        </div>

                        <div className="col-md-6 d-flex flex-wrap gap-2">
                            <button
                                type="button"
                                className="btn btn-success"
                                onClick={generateReport}
                                disabled={loading || !fromValue || !toValue}
                            >
                                <i
                                    className={
                                        loading
                                            ? "spinner-border spinner-border-sm me-2"
                                            : "fa-solid fa-magnifying-glass me-2"
                                    }
                                />
                                {loading ? "Generating..." : "Generate Report"}
                            </button>

                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={handleClear}
                            >
                                Clear
                            </button>

                            <div className="btn-group">
                                <button
                                    type="button"
                                    className="btn btn-outline-success"
                                    onClick={() => applyPreset("today")}
                                >
                                    Today
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-outline-success"
                                    onClick={() => applyPreset("yesterday")}
                                >
                                    Yesterday
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-outline-success"
                                    onClick={() => applyPreset("7d")}
                                >
                                    Last 7 days
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            )}

            {report && (
                <>
                    <div className="row g-3 mb-4">
                        {cards.map((card) => (
                            <div
                                className="col-6 col-md"
                                key={card.label}
                            >
                                <div
                                    className="card h-100 border-0 shadow-sm"
                                    style={{
                                        borderTop: `3px solid ${card.color}`,
                                    }}
                                >
                                    <div className="card-body">
                                        <div className="text-muted small">
                                            {card.label}
                                        </div>

                                        <div
                                            className="fw-bold mt-2"
                                            style={{
                                                color: card.color,
                                                fontSize: "1.2rem",
                                                overflowWrap: "anywhere",
                                            }}
                                        >
                                            {card.value}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white py-3">
                            <strong>Drug-wise Stock Movement</strong>

                            <div className="text-muted small mt-1">
                                {displayDateTime(report.from)} to{" "}
                                {displayDateTime(report.to)}
                                {" · "}
                                {rows.length} product/batch rows
                            </div>
                        </div>

                        <div className="card-body">
                            {rows.length > 0 ? (
                                <DataGrid
                                    rows={rows}
                                    columns={COLUMNS}
                                    getRowId={(row) => row.id}
                                    autoHeight
                                    disableRowSelectionOnClick
                                    showToolbar
                                    slots={{ toolbar: GridToolbar }}
                                    initialState={{
                                        pagination: {
                                            paginationModel: {
                                                pageSize: 25,
                                                page: 0,
                                            },
                                        },
                                        sorting: {
                                            sortModel: [
                                                {
                                                    field: "product",
                                                    sort: "asc",
                                                },
                                            ],
                                        },
                                    }}
                                    pageSizeOptions={[10, 25, 50, 100]}
                                    sx={GRID_SX}
                                />
                            ) : (
                                <div className="alert alert-info mb-0">
                                    No GRN receipts or sales were found for
                                    the selected period.
                                </div>
                            )}
                        </div>
                    </div>

                    {diag && (
                        <div className="text-muted small mt-3">
                            <strong>Report diagnostics</strong>
                            <div>
                                Stock records: {diag.stockRecords}
                                {" · "}
                                Unique stock batches: {diag.uniqueStockBatches}
                            </div>
                            <div>
                                GRN records: {diag.grnRecords}
                                {" · "}
                                Valid GRN dates: {diag.validGrnDates}
                                {" · "}
                                Matched GRN lines: {diag.grnLines}
                            </div>
                            <div>
                                Sales records: {diag.salesRecords}
                                {" · "}
                                Valid sale dates: {diag.validSaleDates}
                                {" · "}
                                Matched sale lines: {diag.salesLines}
                            </div>
                            <div>
                                Unmatched sales lines: {diag.unmatchedSales}
                            </div>
                        </div>
                    )}

                    <div className="text-muted small mt-3">
                        Stock In is calculated from matched GRN receipt
                        quantities within the selected date/time range.
                        Historical balances are estimates unless all purchase
                        receipts, returns, transfers, wastage and adjustments
                        are included. Confirm the GRN serializer field names
                        and reconcile balances against the pharmacy ledger.
                    </div>
                </>
            )}
        </section>
    );
};

export default DrugMovementRegister;